<template>
  <!-- Same skeleton as the RFQs page (specs/price-lists.md, specs/rfqs.md):
       no page box or title strip — the navbar breadcrumb names the page —
       an actions card, then the grid itself carrying the card radius. -->
  <div class="priceLists-vue-wrapper m-2 md:m-4 md:mt-2.5 md:mr-5 w-auto flex flex-col gap-2 md:gap-3">
    <div class="priceLists-header vp-card flex items-center flex-wrap gap-2 sm:gap-4 p-2.5 md:p-3">
      <!-- Partner filter: exact match against the price list's partners. A
           native select (not the old free-text combobox) so it is one tap
           on phones and matches the SO details status select. -->
      <select
        v-model="partnerFilter"
        class="partner-field select select-sm select-bordered focus:outline-none bg-white border-gray-300 text-sm font-normal"
        aria-label="Filter by partner"
      >
        <option value="">All partners</option>
        <option v-for="partner in usedPartners" :key="partner" :value="partner">{{ partner }}</option>
      </select>
      <!-- Part number filter: same markup as the RFQs quick search. Matches
           the part numbers inside each price list (ranges, statics, series). -->
      <div class="search-field relative">
        <i
          class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none"
        ></i>
        <input
          v-model="partNumberFilter"
          type="text"
          placeholder="Filter by part number"
          class="input input-sm input-bordered w-full pl-8 pr-8 text-sm font-normal focus:outline-none bg-white border-gray-300"
        />
        <button
          v-if="partNumberFilter"
          type="button"
          aria-label="Clear part number filter"
          class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          @click="partNumberFilter = ''"
        >
          <i class="fa-solid fa-circle-xmark"></i>
        </button>
      </div>
      <label class="expired-toggle flex items-center gap-2 cursor-pointer whitespace-nowrap">
        <input v-model="showExpired" type="checkbox" class="toggle toggle-primary toggle-sm" />
        <span class="text-sm text-neutral hidden sm:inline">Show expired</span>
        <span class="text-sm text-neutral sm:hidden">Expired</span>
      </label>
      <!-- Primary action, pinned to the right edge of the card at every size. -->
      <!-- Same size as Post to Odoo on SO details (btn-sm, 32px) — it is the
           page's primary action and lines up with the 32px filter inputs. -->
      <button
        type="button"
        class="create-button btn btn-sm btn-primary gap-2 rounded-md normal-case text-white"
        title="New Price List"
        @click="createNewPriceList"
      >
        <i class="fa-solid fa-plus"></i>
        <span class="font-semibold hidden sm:inline">New Price List</span>
        <span class="font-semibold sm:hidden">New</span>
      </button>
    </div>

    <!-- The grid is the outermost box of the table area (no wrapping card);
         flex-1 lets it absorb the viewport height under the actions card. -->
    <div class="tabContainer tablesWrapper flex-1 min-h-0">
      <div v-if="isLoading || errorMessage" class="h-full p-5 py-10 bg-white border border-gray-200 rounded-xl">
        <div v-if="isLoading" class="flex flex-row text-center text-primary justify-center items-center">
          <i class="fa-solid fa-arrows-rotate"></i>
          <p class="ml-2 mb-[0.5px] font-semibold">Loading price lists...</p>
        </div>
        <div v-else class="flex flex-row text-center justify-center items-center text-red-500">
          <i class="fa-solid fa-circle-exclamation"></i>
          <p class="ml-2 mb-[0.5px] font-semibold">{{ errorMessage }}</p>
        </div>
      </div>
      <div v-else class="h-full">
        <!-- Height as style, not class: DataGrid forwards `class` to the
             inner grid element (see the same note in SaleOrdersView). -->
        <DataGrid
          :columnsDefs="columnsDefsComputed"
          :rowsData="filteredPriceLists"
          :rowClassRules="rowClassRules"
          class="ag-theme-alpine"
          style="height: 100%"
        >
          <template #empty>
            <template v-if="!priceLists.length">
              <i class="mdi mdi-format-list-bulleted text-4xl mb-2"></i>
              <span class="font-medium">No price lists yet</span>
              <span class="text-sm mt-1">Create one with New Price List.</span>
            </template>
            <template v-else>
              <i class="mdi mdi-file-search-outline text-4xl mb-2"></i>
              <span class="font-medium">No price lists match</span>
              <span class="text-sm mt-1">Try another partner or part number, or show expired lists.</span>
            </template>
          </template>
        </DataGrid>
      </div>
    </div>
  </div>

  <ConfirmationModal
    v-if="showDeleteConfirmation"
    confirmButtonText="Yes"
    cancelButtonText="No"
    title="Delete Price List"
    message="Are you sure you want to delete this Price List?"
    @confirm="confirmDelete"
    @cancel="cancelDelete"
  />
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import DataGrid from '@/components/DataGrid/DataGrid.vue';
import PriceListActions from '@/components/DataGrid/Templates/PriceListActions.vue';
import PriceListUpdated from '@/components/DataGrid/Templates/PriceListUpdated.vue';
import PriceListDateHeader from '@/components/DataGrid/Templates/PriceListDateHeader.vue';
import ConfirmationModal from '@/components/ConfirmationModal.vue';
import { dispatchToast } from '@/utils/dispatchToast';
import { getPriceLists, deletePriceList, exportPriceList } from '@/services/priceList.service';
import { useCompactTables } from '@/composables/useCompactTables';
import { filterPriceLists, usedPartnerNames } from '@/utils/priceListFilters';

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

const compareDates = (a, b) => new Date(a).getTime() - new Date(b).getTime();

const isExpired = (dateString) => new Date(dateString) < new Date();

export default {
  name: 'PriceListsView',
  components: { DataGrid, ConfirmationModal },
  setup() {
    const router = useRouter();
    // Compact column set only on phones held upright; landscape shows the
    // full table (specs/mobile.md → Orientation).
    const compactTables = useCompactTables();

    const priceLists = ref([]);
    const isLoading = ref(true);
    const errorMessage = ref('');

    const partnerFilter = ref('');
    const partNumberFilter = ref('');
    const showExpired = ref(false);

    const showDeleteConfirmation = ref(false);
    const itemToDelete = ref(null);

    // ========================= Data =========================
    const fetchPriceLists = async () => {
      isLoading.value = true;
      errorMessage.value = '';
      try {
        const data = await getPriceLists({ attributes: ['id', 'name', 'tag', 'active', 'relationshipSegment'] });
        priceLists.value = Array.isArray(data) ? data : [];
      } catch (error) {
        errorMessage.value = 'Error fetching Price Lists';
        dispatchToast('Error fetching Price Lists', { type: 'error' });
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(fetchPriceLists);

    // ========================= Filters =========================
    // Matching rules live in @/utils/priceListFilters so they can be tested
    // without mounting the view.
    const usedPartners = computed(() => usedPartnerNames(priceLists.value));

    const filteredPriceLists = computed(() =>
      filterPriceLists(priceLists.value, {
        partner: partnerFilter.value,
        partNumber: partNumberFilter.value,
        showExpired: showExpired.value,
      }),
    );

    // ========================= Actions =========================
    const editItem = (item) => router.push(`/home/price-lists/${item.id}`);

    const deleteItem = (item) => {
      itemToDelete.value = item;
      showDeleteConfirmation.value = true;
    };

    const confirmDelete = async () => {
      try {
        await deletePriceList(itemToDelete.value.id);
        dispatchToast('Price List deleted successfully', { type: 'success' });
        await fetchPriceLists();
      } catch (error) {
        dispatchToast('Error deleting Price List', { type: 'error' });
      } finally {
        showDeleteConfirmation.value = false;
        itemToDelete.value = null;
      }
    };

    const cancelDelete = () => {
      showDeleteConfirmation.value = false;
      itemToDelete.value = null;
    };

    const onExport = async (item) => {
      try {
        await exportPriceList(item);
        dispatchToast('Export success', { type: 'success' });
      } catch (error) {
        dispatchToast(error.message, { type: 'error' });
      }
    };

    const createNewPriceList = () => router.push('/home/price-lists/new');

    // ========================= Grid =========================
    // Plain MM/DD/YYYY cells. The note about these being Chicago boundaries
    // describes the whole column, so it sits behind an (i) in the HEADER
    // (PriceListDateHeader.vue) rather than on every cell — which also keeps
    // the cells free of any hover fill. The custom header is hover-only, so
    // the compact (portrait phone) set keeps AG Grid's plain header.
    const dateColumn = (headerName, field, extra = {}) => ({
      headerName,
      field,
      minWidth: 130,
      valueFormatter: (params) => (params.value ? formatDate(params.value) : '-'),
      comparator: compareDates,
      ...(compactTables.value
        ? {}
        : {
            headerComponent: PriceListDateHeader,
            headerComponentParams: { boundary: field === 'startDate' ? 'start' : 'end' },
          }),
      ...extra,
    });

    // Record history, deliberately relative ("2 days ago") so it cannot be
    // mistaken for the absolute validity dates beside it — that contrast is
    // what keeps three date columns from reading as a wall. The exact
    // timestamp and the creation date ride along in the cell's popover.
    const lastUpdatedColumn = () => ({
      headerName: 'Last Updated',
      field: 'updatedAt',
      minWidth: 150,
      comparator: compareDates,
      // No cellClass: the popover is attached to the text itself, so the cell
      // stays unshaded on hover.
      cellRenderer: PriceListUpdated,
    });

    const actionsColumn = (width) => ({
      headerName: 'Actions',
      field: 'actions',
      flex: 0,
      width,
      minWidth: width,
      maxWidth: width,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      cellClass: 'actions-cell',
      cellRenderer: PriceListActions,
      cellRendererParams: {
        onEdit: editItem,
        onDelete: deleteItem,
        onExport,
        showExport: !compactTables.value,
      },
    });

    const columnsDefsComputed = computed(() => {
      if (compactTables.value) {
        // Phones: what you need to find and act on a list — export is a
        // desktop task (file download), Tag and Start Date are dropped.
        return [
          { headerName: 'Price List Name', field: 'name', flex: 1, minWidth: 150 },
          dateColumn('Valid Until', 'endDate', { minWidth: 125, sort: 'desc' }),
          actionsColumn(96),
        ];
      }
      return [
        { headerName: 'Price List Name', field: 'name', flex: 2, minWidth: 220 },
        { headerName: 'Tag', field: 'tag', minWidth: 110, valueFormatter: (params) => params.value || '-' },
        dateColumn('Valid From', 'startDate'),
        dateColumn('Valid Until', 'endDate', { sort: 'desc' }),
        lastUpdatedColumn(),
        actionsColumn(124),
      ];
    });

    const rowClassRules = {
      expired: (params) => Boolean(params?.data?.endDate) && isExpired(params.data.endDate),
    };

    return {
      priceLists,
      isLoading,
      errorMessage,
      partnerFilter,
      partNumberFilter,
      showExpired,
      usedPartners,
      filteredPriceLists,
      columnsDefsComputed,
      rowClassRules,
      showDeleteConfirmation,
      confirmDelete,
      cancelDelete,
      createNewPriceList,
    };
  },
};
</script>

<style lang="scss" scoped>
// Full content-area height minus the wrapper's own vertical margins (same
// figures as the RFQs page), so the grid bottom hugs the window bottom.
.priceLists-vue-wrapper {
  height: calc(100% - 16px);
}

@media (min-width: 768px) {
  .priceLists-vue-wrapper {
    height: calc(100% - 26px);
  }
}

// Grid = outermost box of the table area: its own border takes the vp-card
// radius/color, and the header row uses the card fill (as on RFQs).
.tabContainer :deep(.ag-root-wrapper) {
  border-radius: 12px;
  border-color: #e5e7eb; // Tailwind gray-200, same as .vp-card
  overflow: hidden;
}

.tabContainer :deep(.ag-theme-alpine) {
  --ag-header-background-color: var(--vp-card-bg);
}

// The actions cell centres its icon buttons vertically. AG Grid cells are
// block-level with a fixed height; a percentage height on the renderer's
// root never resolves, so the cell itself becomes the flex container.
.tabContainer :deep(.ag-cell.actions-cell) {
  display: flex;
  align-items: center;
}

// Expired lists read as inactive: light fill, muted text.
.tabContainer :deep(.ag-row.expired) {
  background-color: #f9fafb;
  color: #6b7280;
}

.create-button {
  margin-left: auto;
}

.partner-field {
  width: 12rem;
}

.search-field {
  width: 18rem;
}

// Phones: two rows — partner select · New button, then part number filter ·
// Expired toggle. Orders reflow the markup (partner, search, toggle, button).
@media (max-width: 639.98px) {
  // A 65% basis guarantees the select + New button fill row 1 alone, so the
  // part number filter always starts row 2 (next to the toggle) — even on
  // wide phones / small tablets just under the `sm` breakpoint.
  .partner-field {
    order: 0;
    flex: 1 1 65%;
    width: auto;
  }

  .create-button {
    order: 1;
  }

  .search-field {
    order: 2;
    flex: 1 1 10rem;
    width: auto;
  }

  .expired-toggle {
    order: 3;
  }
}
</style>
